import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';

const VARIANT_CONFIG = {
  danger: {
    icon: 'fa-exclamation-triangle',
    heading: 'Please fix the following issues:',
    linkClass: 'text-danger',
  },
  warning: {
    icon: 'fa-info-circle',
    heading: 'Please complete the following fields:',
    linkClass: 'text-warning-emphasis',
  },
};

const GROUP_HEADINGS = {
  attendance: 'Committee Members and Conflicts of Interest',
  candidates: 'Reports on Shortlisted Candidates',
};

const renderLink = (error, linkClass, fieldPrefix) => {
  const domId = `field-${fieldPrefix}${error.field}`;
  return (
    <li key={error.field}>
      <a
        href={`#${domId}`}
        className={linkClass}
        onClick={(e) => {
          e.preventDefault();
          document
            .getElementById(domId)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      >
        {error.message}
      </a>
    </li>
  );
};

const ValidationSummary = forwardRef(
  ({ errors, variant = 'danger', fieldPrefix = '' }, ref) => {
    if (!errors || errors.length === 0) return null;

    const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.danger;

    // Split errors into ungrouped (text fields) and grouped (attendance, candidates)
    const ungrouped = errors.filter((e) => !e.group);
    const grouped = {};
    errors.forEach((e) => {
      if (e.group) {
        if (!grouped[e.group]) grouped[e.group] = [];
        grouped[e.group].push(e);
      }
    });

    return (
      <Alert variant={variant} className="mb-4" ref={ref}>
        <Alert.Heading className="h6">
          <i className={`fas ${config.icon} me-2`} />
          {config.heading}
        </Alert.Heading>

        {ungrouped.length > 0 && (
          <ul className="mb-0 ps-3">
            {ungrouped.map((error) =>
              renderLink(error, config.linkClass, fieldPrefix),
            )}
          </ul>
        )}

        {Object.entries(grouped).map(([groupKey, groupErrors]) => (
          <div
            key={groupKey}
            className={
              ungrouped.length > 0 || Object.keys(grouped).indexOf(groupKey) > 0
                ? 'mt-3'
                : ''
            }
          >
            <strong className="d-block mb-1">
              {GROUP_HEADINGS[groupKey] || groupKey}
            </strong>
            <ul className="mb-0 ps-3">
              {groupErrors.map((error) =>
                renderLink(error, config.linkClass, fieldPrefix),
              )}
            </ul>
          </div>
        ))}
      </Alert>
    );
  },
);

ValidationSummary.displayName = 'ValidationSummary';

ValidationSummary.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      group: PropTypes.string,
    }),
  ).isRequired,
  variant: PropTypes.oneOf(['danger', 'warning']),
  fieldPrefix: PropTypes.string,
};

ValidationSummary.defaultProps = {
  variant: 'danger',
  fieldPrefix: '',
};

export default ValidationSummary;
